import { useTrialExtensionTaskActions } from '@/hooks/useTrialExtensionTaskActions';
import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { trackModalViewed } from '@/lib/analytics/scribe/ui';
import type { GamifyTrialExtensionResponse } from '@heidi/commerce/contracts';
import { reopenTrialExtensionModalAfterActionAtom } from '@heidi/commerce/data';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { isTauri } from '@heidi/runtime/platform';
import { ExtensionTaskId } from '@heidi/schema/ml-scribe';
import { isSavingModalOpenAtom } from '@heidi/templates';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

export function useTrialExtensionModalHandlers(
  isOpen: boolean,
  data: GamifyTrialExtensionResponse | undefined,
  setIsOpen: (open: boolean) => void
) {
  const isSavingModalOpen = useAtomValue(isSavingModalOpenAtom);
  const [shouldReopenAfterAction, setShouldReopenAfterAction] = useAtom(
    reopenTrialExtensionModalAfterActionAtom
  );
  const prevSavingModalOpen = useRef(isSavingModalOpen);
  // Task CTAs navigate away and often autofocus another surface (e.g. Work
  // Home composer). Radix restores focus to the dialog trigger on close, which
  // would blur that surface — suppress only for task-driven closes.
  const closedByTaskActionRef = useRef(false);

  const closeModal = useCallback(
    (fromTaskAction: boolean) => {
      // Each close owns the flag. A task close that never receives
      // onCloseAutoFocus must not suppress focus return on a later plain close.
      closedByTaskActionRef.current = fromTaskAction;
      trackButtonClicked({
        screenName: ScreenName.TrialExtensionModal,
        action: ButtonAction.TrialExtensionClose,
        label: 'Close',
      });
      setIsOpen(false);
    },
    [setIsOpen]
  );

  const handleClose = useCallback(() => {
    closeModal(false);
  }, [closeModal]);

  const { getTaskHandler, completeDesktopAppDownloadTask } = useTrialExtensionTaskActions(
    {
      onBeforeAction: () => {
        closeModal(true);
      },
    }
  );

  const handleCloseAutoFocus = useCallback((event: Event) => {
    if (!closedByTaskActionRef.current) return;
    closedByTaskActionRef.current = false;
    event.preventDefault();
  }, []);

  useEffect(() => {
    const savingModalJustClosed = prevSavingModalOpen.current && !isSavingModalOpen;
    prevSavingModalOpen.current = isSavingModalOpen;
    if (savingModalJustClosed && shouldReopenAfterAction) {
      setShouldReopenAfterAction(false);
      setIsOpen(true);
    }
  }, [isSavingModalOpen, shouldReopenAfterAction, setShouldReopenAfterAction, setIsOpen]);

  useEffect(() => {
    if (isOpen && data?.eligible) {
      trackModalViewed({
        screenName: ScreenName.TrialExtensionModal,
      });
    }
  }, [isOpen, data?.eligible]);

  // Desktop-app users are already running the app the download_desktop_app
  // task asks them to download — auto-complete it instead of leaving it
  // permanently incomplete for them. hasAutoCompletedRef guards against
  // re-firing while this hook instance stays mounted (e.g. a background
  // refetch handing back a new `data.tasks` reference before the mutation's
  // ack lands); the `!completed` check is the belt-and-braces guard across a
  // close/reopen (the modal fully unmounts, resetting the ref) — by the time
  // it reopens, either the ack landed and `completed` is now true, or it's
  // still false and this safely fires once more. At 0% rollout the task is
  // absent from data.tasks entirely, so this is a no-op until backend ramps
  // the flag.
  const hasAutoCompletedDesktopDownloadRef = useRef(false);
  useEffect(() => {
    if (!isOpen || !isTauri() || hasAutoCompletedDesktopDownloadRef.current) return;
    const downloadTask = data?.tasks?.find(
      (task) => task.id === ExtensionTaskId.download_desktop_app
    );
    if (downloadTask && !downloadTask.completed) {
      hasAutoCompletedDesktopDownloadRef.current = true;
      completeDesktopAppDownloadTask();
    }
  }, [isOpen, data?.tasks, completeDesktopAppDownloadTask]);

  const handleTaskClick = useCallback((taskId: ExtensionTaskId) => {
    trackButtonClicked({
      screenName: ScreenName.TrialExtensionModal,
      action: ButtonAction.TrialExtensionTaskClicked,
      label: 'Task clicked',
      taskId,
    });
  }, []);

  return { handleClose, handleCloseAutoFocus, getTaskHandler, handleTaskClick };
}
